import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Link } from "wouter";
import {
  Route, Plus, ArrowLeft, Trash2, Edit, Shield, Users, Clock,
  MapPin, ChevronRight, AlertTriangle
} from "lucide-react";
import CommandNav from "@/components/CommandNav";

function RuleFormDialog({ onSuccess, editRule }: {
  onSuccess: () => void;
  editRule?: any;
}) {
  const [open, setOpen] = useState(false);
  const [sector, setSector] = useState(editRule?.sector || "");
  const [block, setBlock] = useState(editRule?.block || "");
  const [seatStart, setSeatStart] = useState(editRule?.seatStart?.toString() || "");
  const [seatEnd, setSeatEnd] = useState(editRule?.seatEnd?.toString() || "");
  const [primaryAgentId, setPrimaryAgentId] = useState(editRule?.primaryAgentId?.toString() || "");
  const [backupAgentId, setBackupAgentId] = useState(editRule?.backupAgentId?.toString() || "");
  const [supervisorId, setSupervisorId] = useState(editRule?.supervisorId?.toString() || "");
  const [slaSeconds, setSlaSeconds] = useState(editRule?.slaSeconds?.toString() || "120");
  const [status, setStatus] = useState(editRule?.status || "active");

  const agents = trpc.agent.list.useQuery();
  const createMutation = trpc.routing.create.useMutation({
    onSuccess: () => { toast.success("Regra criada com sucesso"); setOpen(false); onSuccess(); },
    onError: (e) => toast.error(e.message),
  });
  const updateMutation = trpc.routing.update.useMutation({
    onSuccess: () => { toast.success("Regra atualizada"); setOpen(false); onSuccess(); },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = () => {
    if (!sector || !seatStart || !seatEnd || !primaryAgentId) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    const data = {
      sector,
      block: block || undefined,
      seatStart: parseInt(seatStart),
      seatEnd: parseInt(seatEnd),
      primaryAgentId: parseInt(primaryAgentId),
      backupAgentId: backupAgentId ? parseInt(backupAgentId) : undefined,
      supervisorId: supervisorId ? parseInt(supervisorId) : undefined,
      slaSeconds: parseInt(slaSeconds) || 120,
      status: status as "active" | "inactive",
    };

    if (editRule) {
      updateMutation.mutate({ id: editRule.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editRule ? (
          <Button variant="ghost" size="icon"><Edit className="w-4 h-4" /></Button>
        ) : (
          <Button className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" /> Nova Regra
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white">
            {editRule ? "Editar Regra" : "Nova Regra de Roteamento"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Setor *</Label>
              <Input value={sector} onChange={e => setSector(e.target.value)}
                placeholder="Ex: Norte A" className="bg-slate-800 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Bloco</Label>
              <Input value={block} onChange={e => setBlock(e.target.value)}
                placeholder="Ex: B1" className="bg-slate-800 border-slate-600 text-white" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Cadeira Inicial *</Label>
              <Input type="number" value={seatStart} onChange={e => setSeatStart(e.target.value)}
                placeholder="1" className="bg-slate-800 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Cadeira Final *</Label>
              <Input type="number" value={seatEnd} onChange={e => setSeatEnd(e.target.value)}
                placeholder="500" className="bg-slate-800 border-slate-600 text-white" />
            </div>
          </div>

          <div>
            <Label className="text-slate-300">Agente Principal *</Label>
            <Select value={primaryAgentId} onValueChange={setPrimaryAgentId}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Selecione o agente principal" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {agents.data?.map(a => (
                  <SelectItem key={a.id} value={a.id.toString()} className="text-white">
                    {a.fullName} {a.sector ? `(${a.sector})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-300">Agente Reserva</Label>
            <Select value={backupAgentId} onValueChange={setBackupAgentId}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Selecione o agente reserva" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="none" className="text-white">Nenhum</SelectItem>
                {agents.data?.map(a => (
                  <SelectItem key={a.id} value={a.id.toString()} className="text-white">
                    {a.fullName} {a.sector ? `(${a.sector})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-300">Supervisor</Label>
            <Select value={supervisorId} onValueChange={setSupervisorId}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Selecione o supervisor" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="none" className="text-white">Nenhum</SelectItem>
                {agents.data?.map(a => (
                  <SelectItem key={a.id} value={a.id.toString()} className="text-white">
                    {a.fullName} {a.sector ? `(${a.sector})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">SLA (segundos)</Label>
              <Input type="number" value={slaSeconds} onChange={e => setSlaSeconds(e.target.value)}
                placeholder="120" className="bg-slate-800 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="active" className="text-white">Ativa</SelectItem>
                  <SelectItem value="inactive" className="text-white">Inativa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={handleSubmit} className="w-full bg-purple-600 hover:bg-purple-700"
            disabled={createMutation.isPending || updateMutation.isPending}>
            {(createMutation.isPending || updateMutation.isPending) ? "Salvando..." : editRule ? "Atualizar" : "Criar Regra"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RoutingRules() {
  const rules = trpc.routing.list.useQuery();
  const deleteMutation = trpc.routing.delete.useMutation({
    onSuccess: () => { toast.success("Regra removida"); rules.refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const activeRules = rules.data?.filter(r => r.status === "active") || [];
  const totalSectors = new Set(rules.data?.map(r => r.sector)).size;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-white">
      {/* Header — ELLA Command Platform */}
      <div className="sticky top-0 z-50">
        <CommandNav
          pageTitle="Regras de Roteamento"
          rightContent={
            <RuleFormDialog onSuccess={() => rules.refetch()} />
          }
        />
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900/50 border-slate-700/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Route className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{rules.data?.length || 0}</p>
                <p className="text-xs text-slate-400">Total de Regras</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-700/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Shield className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{activeRules.length}</p>
                <p className="text-xs text-slate-400">Regras Ativas</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-700/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <MapPin className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{totalSectors}</p>
                <p className="text-xs text-slate-400">Setores Cobertos</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-700/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {activeRules.length > 0 ? Math.round(activeRules.reduce((s, r) => s + r.slaSeconds, 0) / activeRules.length) : 0}s
                </p>
                <p className="text-xs text-slate-400">SLA Médio</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Banner */}
        <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-purple-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-purple-200 font-medium">Como funciona o roteamento</p>
            <p className="text-xs text-purple-300/70 mt-1">
              Quando um evento é detectado em um setor/faixa de cadeiras, o sistema localiza automaticamente a regra correspondente
              e aciona o agente principal. Se o agente não responder dentro do SLA, o sistema redireciona para o agente reserva
              e, em seguida, para o supervisor.
            </p>
          </div>
        </div>

        {/* Rules List */}
        {rules.isLoading ? (
          <div className="text-center py-12 text-slate-400">Carregando regras...</div>
        ) : rules.data?.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-700/50">
            <CardContent className="p-12 text-center">
              <Route className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-2">Nenhuma regra de roteamento cadastrada</p>
              <p className="text-xs text-slate-500 mb-4">Crie regras para definir qual agente atende cada setor/faixa</p>
              <RuleFormDialog onSuccess={() => rules.refetch()} />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {rules.data?.map(rule => (
              <Card key={rule.id} className="bg-slate-900/50 border-slate-700/50 hover:border-purple-700/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-purple-500/20">
                        <MapPin className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white">{rule.sector}</span>
                          {rule.block && <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">Bloco {rule.block}</Badge>}
                          <Badge variant={rule.status === "active" ? "default" : "secondary"}
                            className={rule.status === "active" ? "bg-green-600/20 text-green-400 border-green-600/30" : "bg-slate-600/20 text-slate-400"}>
                            {rule.status === "active" ? "Ativa" : "Inativa"}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          Cadeiras {rule.seatStart} — {rule.seatEnd}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Agent Chain */}
                      <div className="flex items-center gap-2 text-xs">
                        <div className="text-center">
                          <p className="text-slate-500">Principal</p>
                          <p className="text-purple-300 font-medium">{rule.primaryAgentName}</p>
                        </div>
                        {rule.backupAgentName && (
                          <>
                            <ChevronRight className="w-3 h-3 text-slate-600" />
                            <div className="text-center">
                              <p className="text-slate-500">Reserva</p>
                              <p className="text-amber-300 font-medium">{rule.backupAgentName}</p>
                            </div>
                          </>
                        )}
                        {rule.supervisorName && (
                          <>
                            <ChevronRight className="w-3 h-3 text-slate-600" />
                            <div className="text-center">
                              <p className="text-slate-500">Supervisor</p>
                              <p className="text-red-300 font-medium">{rule.supervisorName}</p>
                            </div>
                          </>
                        )}
                      </div>

                      {/* SLA */}
                      <div className="text-center">
                        <p className="text-xs text-slate-500">SLA</p>
                        <p className="text-sm font-mono text-amber-400">{rule.slaSeconds}s</p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <RuleFormDialog editRule={rule} onSuccess={() => rules.refetch()} />
                        <Button variant="ghost" size="icon"
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          onClick={() => {
                            if (confirm("Remover esta regra?")) {
                              deleteMutation.mutate({ id: rule.id });
                            }
                          }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
